<script setup lang="ts">
import { Eye, Handshake, Lock, Pause, ShieldCheck } from '@lucide/vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const modes = [
    {
        title: 'Assist me',
        description:
            'Your assistant creates drafts and suggestions. You publish everything yourself.',
        default: false,
    },
    {
        title: 'Review first',
        description:
            'It plans and prepares content, and waits for your approval before anything goes out.',
        default: true,
    },
    {
        title: 'Auto-publish',
        description:
            'It publishes on the channels and content types you explicitly allow, within the rules you set.',
        default: false,
    },
];

const guarantees = [
    {
        icon: ShieldCheck,
        text: 'Sensitive website edits, deletions and pricing changes need confirmation',
    },
    {
        icon: Eye,
        text: 'Every action is recorded, so you can always see what happened and why',
    },
    {
        icon: Pause,
        text: 'Pause the assistant at any time, per business or per channel',
    },
    {
        icon: Lock,
        text: 'Tool permissions are yours to grant, review and revoke',
    },
];
</script>

<template>
    <section id="automation" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                eyebrow="You stay in control"
                title="Choose how much your assistant does."
                description="Start cautious and loosen the reins when you are comfortable. Nothing changes that you did not allow."
            />

            <div class="mt-12 grid gap-6 lg:grid-cols-3">
                <div
                    v-for="mode in modes"
                    :key="mode.title"
                    class="bg-card border-border flex h-full flex-col gap-3 rounded-xl border p-6"
                    :class="
                        mode.default
                            ? 'border-primary/30 ring-primary/15 ring-1'
                            : ''
                    "
                >
                    <div class="flex items-center gap-2">
                        <span
                            class="bg-primary/10 text-primary inline-flex size-9 items-center justify-center rounded-lg"
                        >
                            <Handshake class="size-4" aria-hidden="true" />
                        </span>
                        <h3 class="text-lg">{{ mode.title }}</h3>
                    </div>
                    <span
                        v-if="mode.default"
                        class="border-primary/30 bg-primary/10 w-fit rounded-full border px-2 py-0.5 text-xs font-medium"
                    >
                        Default
                    </span>
                    <p class="text-muted-foreground text-sm leading-relaxed">
                        {{ mode.description }}
                    </p>
                </div>
            </div>

            <ul class="mt-10 grid gap-4 sm:grid-cols-2">
                <li
                    v-for="item in guarantees"
                    :key="item.text"
                    class="flex items-start gap-3 text-sm"
                >
                    <component
                        :is="item.icon"
                        class="text-secondary mt-0.5 size-4 shrink-0"
                        aria-hidden="true"
                    />
                    {{ item.text }}
                </li>
            </ul>
        </div>
    </section>
</template>
